import MaterialTable, { Column } from "@material-table/core";
import React, { useState, useEffect } from "react";

import { Globals } from "../../helpers/globals";

import MergeMatterManagmentPage from "./merge";
import CreateMatterManagmentPage from "./create";
import ArchivePage from "./archived";
import EditPage from "./edit";
import ImportPage from "./import";
import { MatterService } from "../../services/ClientService";
import * as APIModels from "../../models";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TableRow,
    Card,
} from "@mui/material";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { CloudUpload, Inventory } from "@mui/icons-material";
import {
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
} from "@material-ui/core";
import {
    MatterMappingReduced,
    MatterReduced,
    reduce,
} from "./MatterManagmentTypes";

import dayjs from "dayjs";
import AppBar from "./AppBar";

export {
    CreateMatterManagmentPage,
    MergeMatterManagmentPage,
    ArchivePage,
    EditPage,
    ImportPage,
};
interface FileBuffer {
    fileBuffer: string;
}

function base64ToCsv(fileBuffer: string, filename: string): void {
    const decodedString = atob(fileBuffer); // "atob" is the browser's built-in base64 decoder

    const lines = decodedString.split("\n");
    let header = "";
    for (const value of lines[0].split(",")) {
        header += `${value},`;
    }
    header = header.substring(0, header.length - 1); // remove trailing comma

    const rows = lines.slice(1).map((line) => line.split(","));
    let csvContent = "";
    for (const row of rows) {
        csvContent += `${row.join(",")}\n`;
    }

    const csvStringFull = `Data: ${header}\n${csvContent}`;

    // Save the CSV content to a file
    const blob = new Blob([csvStringFull], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    // Clean up the blob object
    window.URL.revokeObjectURL(url);
}

export default function MatterManagmentPage() {
    const currentUser = Globals.currentUserValue;
    const [organizationId, _] = useState(currentUser.organizationID);
    const [matters, setMatters] = useState([] as Array<MatterReduced>);
    const [fieldsDisplayed, setFieldsDisplayed] =
        useState<Array<MatterMappingReduced>>();
    const [showFieldsModal, setShowFieldsModal] = useState<boolean>(false);
    const [matterNumberDisplaed, setMatterNumberDisplaed] =
        useState<string>("0000000000");
    const [selected, setSelected] = useState<Array<string>>();
    const MatterManagmentColumns: Array<Column<MatterReduced>> = [
        {
            title: "Matter Number",
            field: "matterNumber",
            editable: "never",
            render: (obj) => (
                <Link
                    component="button"
                    variant="h6"
                    onClick={() => {
                        window.location.href = `/matter/${obj.id}/edit`;
                    }}
                >
                    <span>
                        <b>{obj.matterNumber}</b>
                    </span>
                </Link>
            ),
        },
        {
            title: "Matter ID",
            field: "identifier",
            render: (obj) => (
                <span>
                    {obj.identifier.key}: {obj.identifier.value}
                </span>
            ),
            customFilterAndSearch(filter, rowData, _) {
                return filter === rowData.identifier.value;
            },
        },
        {
            title: "Fields",
            customFilterAndSearch(filter, rowData, _) {
                const jsonObject = rowData.mappings;
                return jsonObject.some((mapping) => {
                    const truthy = mapping.value
                        .toLowerCase()
                        .includes(String(filter).toLowerCase());
                    if (truthy) {
                        console.log({
                            truthy: { ...mapping },
                        });
                    }
                    return truthy;
                });
            },
            render: (obj) => (
                <Button
                    color="primary"
                    variant="contained"
                    onClick={() => {
                        setMatterNumberDisplaed(
                            obj.matterNumber ?? "0000000000"
                        );
                        setFieldsDisplayed(obj.mappings);
                        setShowFieldsModal(true);
                    }}
                >
                    Show
                </Button>
            ),
        },
    ];

    useEffect(() => {
        MatterService.getAllV2({
            organizationId: organizationId ?? APIModels.EmptyGuid,
        }).then((res) => {
            if (res.data && res.successful) {
                const objs = res.data.map<MatterReduced>(reduce);
                setMatters(objs);
            } else {
                console.error(res.errorMessage);
            }
        });
    }, [organizationId]);

    const refreshData = () => {
        MatterService.getAllV2({
            organizationId: organizationId ?? APIModels.EmptyGuid,
        }).then((res) => {
            if (res.data && res.successful) {
                const objs = res.data.map<MatterReduced>(reduce);
                setMatters(objs);
            } else {
                console.error(res.errorMessage);
            }
        });
    };

    const renderFields = (obj: Array<MatterMappingReduced>) => {
        const sortedObj = obj.sort((a, b) => {
            return Object.keys(a)
                .join("")
                .localeCompare(Object.keys(b).join(""));
        });
        return (
            <TableBody>
                {sortedObj.map(({ id, key, value }) => (
                    <TableRow key={id}>
                        <TableCell>{key}</TableCell>
                        <TableCell>{value}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        );
    };

    const handleExportClick = async () => {
        const response = await MatterService.exportMatters({
            body: {
                organizationID: Globals.currentUserValue.organizationID,
                matterIDs: selected,
            },
        });
        // eslint-disable-nextline no-use-before-define
        const filename = `${
            Globals.currentUserValue.organizationName
        }-matters-${dayjs(Date.now()).format("DD-MM-YY")}.csv`;
        const fileBuffer = await response;
        return base64ToCsv(fileBuffer, filename);
    };

    const handleGetColumns = async () => {
        const response = await MatterService.getCols({
            organizationId:
                Globals.currentUserValue.organizationID ?? APIModels.EmptyGuid,
        });

        if (response.data && response.successful) {
            const filename = "mergeFieldKeys.csv";
            const delimiter = ",";
            const mergeFieldKeys = response.data
                .map((header) => `${header}`)
                .join(delimiter);
            const fileBuffer = await new Promise((resolve) => {
                const encoder = new TextEncoder();
                const encodedCsv = encoder.encode(mergeFieldKeys);
                resolve(encodedCsv.buffer);
            });

            const headerDownloadLink = document.createElement("a");
            headerDownloadLink.href = URL.createObjectURL(
                new Blob([fileBuffer as ArrayBuffer], {
                    type: "application/vnd.openxmlformats-officedockment.spreadshtml.sheet",
                })
            );
            headerDownloadLink.download = filename;
            headerDownloadLink.click();
        } else {
            response.errorMessage;
        }
    };

    return (
        <div className="content">
            <div className="container-fluid">
                <Dialog
                    open={showFieldsModal}
                    onClose={() => {
                        setShowFieldsModal(false);
                        setFieldsDisplayed(undefined);
                    }}
                >
                    <DialogTitle>{matterNumberDisplaed}</DialogTitle>
                    <DialogContent>
                        <Table>
                            <TableHead>
                                <TableCell>Field</TableCell>
                                <TableCell>Value</TableCell>
                            </TableHead>
                            {fieldsDisplayed && renderFields(fieldsDisplayed)}
                        </Table>
                    </DialogContent>
                    <DialogActions>
                        <Stack direction="row-reverse">
                            <Button
                                color="primary"
                                variant="outlined"
                                onClick={() => setShowFieldsModal(false)}
                            >
                                <Stack direction="row" spacing={2}>
                                    <CloudUpload />
                                    <b></b>
                                </Stack>
                            </Button>
                        </Stack>
                    </DialogActions>
                </Dialog>
                <Stack direction="column" spacing={4} marginTop={4}>
                    <AppBar
                        sx={{ width: "100%", paddingY: 1, paddingX: 2 }}
                        buttonSpacing={6}
                        iconSpacing={6}
                    />
                    <Stack direction={"row"}>
                        <MaterialTable
                            style={{ width: "100%", padding: "1 2" }}
                            columns={MatterManagmentColumns}
                            data={matters}
                            title="Active Matters"
                            actions={[
                                {
                                    icon: () => <MergeTypeIcon />,
                                    tooltip: "Merge",
                                    onClick: (_, rowData) => {
                                        const row = rowData as MatterReduced;
                                        window.location.href = `/matter/${row.id}`;
                                    },
                                },
                                {
                                    icon: () => (
                                        <ContentCopyIcon color="primary" />
                                    ),
                                    tooltip: "Create with these mergefields",
                                    onClick: (_, data) => {
                                        const row = data as MatterReduced;
                                        window.location.href =
                                            "/matter/create?copyFrom=" + row.id;
                                    },
                                },
                                {
                                    icon: () => <Inventory />,
                                    tooltip: "Archive",
                                    onClick: (_, rowData) => {
                                        const row = rowData as MatterReduced;
                                        MatterService.archive({
                                            matterId:
                                                row.id ?? APIModels.EmptyGuid,
                                        })
                                            .then((res) => {
                                                if (
                                                    res.data &&
                                                    !res.successful
                                                ) {
                                                    alert({
                                                        error: res.errorMessage,
                                                        data: res.data,
                                                    });
                                                }
                                                console.log("yippy! ");
                                            })
                                            .then(() => {
                                                refreshData();
                                                console.log("MOTHER FUCKER");
                                            });
                                    },
                                },
                            ]}
                            options={{
                                actionsColumnIndex: -1,
                                pageSizeOptions: [5, 10, 20, 50, 75, 100],
                                exportAllData: true,
                            }}
                        />
                    </Stack>
                    <Card sx={{ width: "100%", paddingX: 2, paddingY: 1 }}>
                        <Stack
                            direction={"row"}
                            spacing={2}
                            justifyContent="left"
                        >
                            <button
                                className="btn btn-primary waves-effect waves-light"
                                style={{ width: "12rem" }}
                                onClick={handleExportClick}
                            >
                                <span>
                                    Export{" "}
                                    {selected?.length === 0 ? (
                                        <b>ALL</b>
                                    ) : (
                                        <b>{selected?.length}</b>
                                    )}{" "}
                                    Matters
                                </span>
                            </button>

                            <button
                                className="btn btn-primary waves-effect waves-light"
                                style={{ width: "12rem" }}
                                onClick={handleGetColumns}
                            >
                                <span>Get Columns</span>
                            </button>
                        </Stack>
                    </Card>
                </Stack>
            </div>
        </div>
    );
}
